import { useState, useEffect, useCallback, useRef } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Days of the week (0 = Sunday, 6 = Saturday)
 */
export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
] as const

/**
 * Recurrence types
 */
export const RECURRENCE_TYPES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
] as const

/**
 * Time range structure
 */
export interface TimeRange {
  id: string
  start_time: string
  end_time: string
  sort_order: number
  duration_minutes?: number
  formatted_range?: string
}

/**
 * Time slot structure from API
 */
export interface TimeSlot {
  id: string
  label: string
  recurrence_type: 'weekly' | 'biweekly' | 'monthly'
  is_active: boolean
  days: number[]
  time_ranges: TimeRange[]
  facility_id: string
  schedule_id: string
  divisions?: string[]
  created_at?: string
  updated_at?: string
}

/**
 * Time slot count per facility
 */
export interface TimeSlotCount {
  facility_id: string
  count: number
}

/**
 * Data for creating a new time slot
 */
export interface CreateTimeSlotData {
  label: string
  recurrence_type: 'weekly' | 'biweekly' | 'monthly'
  days: number[]
  time_ranges: { start_time: string; end_time: string }[]
  divisions?: string[]
}

/**
 * Data for updating a time slot
 */
export interface UpdateTimeSlotData {
  label?: string
  recurrence_type?: 'weekly' | 'biweekly' | 'monthly'
  days?: number[]
  time_ranges?: { id?: string; start_time: string; end_time: string }[]
  is_active?: boolean
  divisions?: string[]
}

/**
 * Returns a fingerprint string for a time slot, used to group identical
 * slots across facilities into a single displayed row.
 */
export const getSlotFingerprint = (slot: TimeSlot): string => {
  const days = [...(slot.days || [])].sort((a, b) => a - b).join(',')
  const tr = slot.time_ranges?.[0]
  const start = tr?.start_time?.slice(0, 5) || ''
  const end = tr?.end_time?.slice(0, 5) || ''
  const divs = [...(slot.divisions || [])].sort().join(',')
  return `${slot.recurrence_type}|${days}|${start}|${end}|${divs}`
}

/**
 * Hook result interface
 */
interface UseSchedulerTimeSlotsResult {
  /** List of time slots for the schedule */
  timeSlots: TimeSlot[]
  /** Time slot counts per facility */
  timeSlotCounts: Record<string, number>
  /** Loading state */
  loading: boolean
  /** Loading state for counts */
  loadingCounts: boolean
  /** Error state */
  error: Error | null
  /** Refetch time slots */
  refetch: () => Promise<void>
  /** Fetch time slot counts */
  fetchCounts: () => Promise<void>
  /** Create a new time slot */
  createTimeSlot: (
    facilityId: string,
    data: CreateTimeSlotData
  ) => Promise<TimeSlot>
  /** Update a time slot */
  updateTimeSlot: (
    timeSlotId: string,
    data: UpdateTimeSlotData
  ) => Promise<TimeSlot>
  /** Delete a time slot */
  deleteTimeSlot: (timeSlotId: string) => Promise<void>
  /** Duplicate a time slot */
  duplicateTimeSlot: (
    timeSlotId: string,
    targetFacilityId?: string
  ) => Promise<TimeSlot>
  /** Get time slots for a specific facility */
  getTimeSlotsForFacility: (facilityId: string) => TimeSlot[]
  /** Create a time slot for every facility in a venue (fan-out) */
  createTimeSlotForVenue: (
    facilityIds: string[],
    data: CreateTimeSlotData
  ) => Promise<void>
  /** Update multiple time slots in parallel */
  updateTimeSlotBatch: (
    timeSlotIds: string[],
    data: UpdateTimeSlotData
  ) => Promise<void>
  /** Delete multiple time slots in parallel */
  deleteTimeSlotBatch: (timeSlotIds: string[]) => Promise<void>
}

/**
 * Custom hook for managing scheduler time slots
 * @param competitionId - The competition/schedule ID to manage time slots for
 * @returns Object containing time slots data, loading states, and CRUD functions
 */
export const useSchedulerTimeSlots = (
  competitionId?: string
): UseSchedulerTimeSlotsResult => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([])
  const [timeSlotCounts, setTimeSlotCounts] = useState<Record<string, number>>(
    {}
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingCounts, setLoadingCounts] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  // Local divisions mapping - preserved across API refetches
  // since the API may not yet return divisions in the response
  const divisionsMapRef = useRef<Record<string, string[]>>({})

  /**
   * Fetches all time slots for the schedule
   */
  const fetchTimeSlots = useCallback(async () => {
    if (!competitionId) {
      setTimeSlots([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await req(`/schedules/${competitionId}/time-slots`)
      const slots: TimeSlot[] = data || []
      // Merge locally stored divisions back into refetched data
      const mergedSlots = slots.map((slot) => ({
        ...slot,
        divisions:
          slot.divisions || divisionsMapRef.current[slot.id] || undefined,
      }))
      setTimeSlots(mergedSlots)
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching time slots')
      )
      setTimeSlots([])
    } finally {
      setLoading(false)
    }
  }, [competitionId])

  /**
   * Fetches time slot counts per facility
   */
  const fetchCounts = useCallback(async () => {
    if (!competitionId) {
      setTimeSlotCounts({})
      return
    }

    setLoadingCounts(true)

    try {
      const { data } = await req(
        `/schedules/${competitionId}/time-slots/counts`
      )
      const counts: Record<string, number> = {}
      if (Array.isArray(data)) {
        data.forEach((item: TimeSlotCount) => {
          if (item.facility_id) {
            counts[item.facility_id] = item.count
          }
        })
      }
      setTimeSlotCounts(counts)
    } catch (err: unknown) {
      // Don't set error for counts - it's secondary data
      console.error('Failed to fetch time slot counts:', err)
    } finally {
      setLoadingCounts(false)
    }
  }, [competitionId])

  /**
   * Creates a new time slot for a facility
   */
  const createTimeSlot = useCallback(
    async (facilityId: string, data: CreateTimeSlotData): Promise<TimeSlot> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const response = await req(
        `/schedules/${competitionId}/facilities/${facilityId}/time-slots`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      )

      // Store divisions locally if provided
      if (data.divisions && data.divisions.length > 0 && response.data?.id) {
        divisionsMapRef.current[response.data.id] = data.divisions
      }

      // Refetch to update the list
      await fetchTimeSlots()
      await fetchCounts()

      return response.data
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Updates an existing time slot
   */
  const updateTimeSlot = useCallback(
    async (timeSlotId: string, data: UpdateTimeSlotData): Promise<TimeSlot> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const response = await req(
        `/schedules/${competitionId}/time-slots/${timeSlotId}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      )

      // Store divisions locally
      if (data.divisions && data.divisions.length > 0) {
        divisionsMapRef.current[timeSlotId] = data.divisions
      } else if (data.divisions !== undefined) {
        // Explicitly cleared divisions
        delete divisionsMapRef.current[timeSlotId]
      }

      // Refetch to update the list
      await fetchTimeSlots()
      await fetchCounts()

      return response.data
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Deletes a time slot
   */
  const deleteTimeSlot = useCallback(
    async (timeSlotId: string): Promise<void> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      await req(`/schedules/${competitionId}/time-slots/${timeSlotId}`, {
        method: 'DELETE',
      })

      // Clean up local divisions data
      delete divisionsMapRef.current[timeSlotId]

      // Refetch to update the list
      await fetchTimeSlots()
      await fetchCounts()
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Duplicates a time slot, optionally to a different facility
   */
  const duplicateTimeSlot = useCallback(
    async (
      timeSlotId: string,
      targetFacilityId?: string
    ): Promise<TimeSlot> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const body = targetFacilityId
        ? JSON.stringify({ facility_id: targetFacilityId })
        : undefined

      const response = await req(
        `/schedules/${competitionId}/time-slots/${timeSlotId}/duplicate`,
        {
          method: 'POST',
          body,
        }
      )

      // Refetch to update the list
      await fetchTimeSlots()
      await fetchCounts()

      return response.data
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Creates a time slot for every facility in a venue (fan-out).
   * Calls POST per facility in parallel, then refetches once.
   */
  const createTimeSlotForVenue = useCallback(
    async (facilityIds: string[], data: CreateTimeSlotData): Promise<void> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const results = await Promise.allSettled(
        facilityIds.map(async (fid) => {
          const response = await req(
            `/schedules/${competitionId}/facilities/${fid}/time-slots`,
            {
              method: 'POST',
              body: JSON.stringify(data),
            }
          )
          if (
            data.divisions &&
            data.divisions.length > 0 &&
            response.data?.id
          ) {
            divisionsMapRef.current[response.data.id] = data.divisions
          }
          return response
        })
      )

      const failures = results.filter((r) => r.status === 'rejected')
      if (failures.length === facilityIds.length) {
        throw new Error('Failed to create time slot for all facilities')
      }

      await fetchTimeSlots()
      await fetchCounts()
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Updates multiple time slots in parallel, then refetches once.
   */
  const updateTimeSlotBatch = useCallback(
    async (timeSlotIds: string[], data: UpdateTimeSlotData): Promise<void> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const results = await Promise.allSettled(
        timeSlotIds.map(async (tsId) => {
          const response = await req(
            `/schedules/${competitionId}/time-slots/${tsId}`,
            {
              method: 'PUT',
              body: JSON.stringify(data),
            }
          )
          if (data.divisions && data.divisions.length > 0) {
            divisionsMapRef.current[tsId] = data.divisions
          } else if (data.divisions !== undefined) {
            delete divisionsMapRef.current[tsId]
          }
          return response
        })
      )

      const failures = results.filter((r) => r.status === 'rejected')
      if (failures.length === timeSlotIds.length) {
        throw new Error('Failed to update all time slots')
      }

      await fetchTimeSlots()
      await fetchCounts()
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Deletes multiple time slots in parallel, then refetches once.
   */
  const deleteTimeSlotBatch = useCallback(
    async (timeSlotIds: string[]): Promise<void> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const results = await Promise.allSettled(
        timeSlotIds.map(async (tsId) => {
          await req(`/schedules/${competitionId}/time-slots/${tsId}`, {
            method: 'DELETE',
          })
          delete divisionsMapRef.current[tsId]
        })
      )

      const failures = results.filter((r) => r.status === 'rejected')
      if (failures.length === timeSlotIds.length) {
        throw new Error('Failed to delete all time slots')
      }

      await fetchTimeSlots()
      await fetchCounts()
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Gets time slots filtered by facility ID
   */
  const getTimeSlotsForFacility = useCallback(
    (facilityId: string): TimeSlot[] => {
      return timeSlots.filter((slot) => slot.facility_id === facilityId)
    },
    [timeSlots]
  )

  // Fetch time slots when competitionId changes
  useEffect(() => {
    if (competitionId) {
      fetchTimeSlots()
      fetchCounts()
    } else {
      setTimeSlots([])
      setTimeSlotCounts({})
    }
  }, [competitionId, fetchTimeSlots, fetchCounts])

  return {
    timeSlots,
    timeSlotCounts,
    loading,
    loadingCounts,
    error,
    refetch: fetchTimeSlots,
    fetchCounts,
    createTimeSlot,
    updateTimeSlot,
    deleteTimeSlot,
    duplicateTimeSlot,
    getTimeSlotsForFacility,
    createTimeSlotForVenue,
    updateTimeSlotBatch,
    deleteTimeSlotBatch,
  }
}

export default useSchedulerTimeSlots
